import * as React from 'react';
import { sliceContent, cn } from '@restworld/utils-common';
export function Paragraph({
  content,
  twStyle,
  children,
  variant,
  font,
  color,
  align,
  standardVariant,
  isPointer,
  maxChars,
}: ParagraphProps) {
  const convertedVariant: StandardParagraphVariant = mapVariantToStandard(variant);
  const convertedContent = maxChars && content ? sliceContent(content, maxChars) : content;

  return (
    <ParagraphUI {...{ twStyle, variant: convertedVariant, font, color, align, standardVariant, isPointer }}>
      {convertedContent || children}
    </ParagraphUI>
  );
}
export default Paragraph;

const ParagraphUI = ({
  twStyle,
  variant,
  color,
  font,
  align = 'left',
  standardVariant,
  isPointer,
  className,
  children,
  ...props
}: Pick<ParagraphProps, 'twStyle' | 'color' | 'font' | 'align' | 'isPointer'> & {
  variant?: StandardParagraphVariant;
  standardVariant?: StandardParagraphVariant;
} & React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cn(
      'text-gray-600 font-normal text-base whitespace-pre-wrap',
      variant && standardParagraphVariants[variant],
      color && colorMap[color],
      align && alignment[align],
      standardVariant && standardParagraphVariants[standardVariant],
      isPointer && 'cursor-pointer',
      font && fontMapping[font],
      twStyle,
      className
    )}
    {...props}
  >
    {children}
  </p>
);

export interface ParagraphProps {
  content?: string;
  twStyle?: string;
  children?: string | React.ReactNode;
  variant?: 'span' | 'body' | 'caption' | 'label' | 'link-text' | 'medium' | 'small' | 'xsmall' | 'body-small';
  color?: keyof typeof colorMap;
  align?: keyof typeof alignment;
  standardVariant?: StandardParagraphVariant;
  font?: keyof typeof fontMapping;
  isPointer?: boolean;
  maxChars?: number;
}

const colorMap = {
  'gray-400': 'text-gray-400',
  'gray-500': 'text-gray-500',
  'gray-600': 'text-gray-600',
  'primary-800': 'text-primary-800',
  black: 'text-black',
  'red-600': 'text-red-600',
  'primary-700': 'text-primary-700',
  'primary-900': 'text-primary-900',
  white: 'text-white',
  'primary-100': 'text-primary-100',
};

const fontMapping = {
  medium: 'font-medium',
  normal: 'font-normal',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const alignment = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

export const standardParagraphVariants = {
  'body-l': 'lg:text-[20px] lg:leading-[30px] text-[20px] leading-[30px] font-normal',
  'body-m': 'lg:text-[16px] lg:leading-[24px] leading-[24px]  text-[16px] font-normal',
  'body-sm': 'lg:text-[14px] text-[14px] lg:leading-[21px] leading-[21px] font-normal',
  'label-m': 'lg:text-[16px] text-[16px] lg:leading-[24px] leading-[24px] font-medium',
  'label-s': 'lg:text-[14px] text-[14px] lg:leading-[21px] leading-[21px] font-semibold',
};

export type StandardParagraphVariant = keyof typeof standardParagraphVariants;

export const mapVariantToStandard = (inputVariant?: ParagraphProps['variant']): StandardParagraphVariant => {
  switch (inputVariant) {
    case 'medium':
      return 'body-l';
    case 'body':
    case 'span':
      return 'body-m';
    case 'body-small':
    case 'caption':
    case 'label':
    case 'link-text':
    case 'small':
    case 'xsmall':
      return 'body-sm';

    default:
      return 'body-m';
  }
};
